import React from 'react';
import { FaUsers, FaUserPlus, FaClipboardCheck, FaTools, FaGlobe, FaShieldAlt } from 'react-icons/fa';

const EngagementModels: React.FC = () => {
  const models = [
    {
      icon: <FaUsers className="text-4xl" />,
      title: 'Dedicated Development Team',
      description: 'Get a fully managed team of senior engineers who become an extension of your organization. Ideal for ongoing product development and long-term partnerships.',
      benefits: [
        'Dedicated team members working exclusively on your project',
        'Direct communication with engineers',
        'Full control over priorities and roadmap',
        'Scale team up or down based on needs',
        'Deep understanding of your business and codebase',
        'Consistent quality and coding standards'
      ],
      bestFor: [
        'Long-term product development',
        'Building and scaling complex platforms',
        'Companies without in-house tech teams',
        'Ongoing feature development and maintenance'
      ],
      engagement: 'Monthly retainer with flexible team composition'
    },
    {
      icon: <FaUserPlus className="text-4xl" />,
      title: 'Staff Augmentation',
      description: 'Augment your existing engineering team with specialized senior developers for specific skills or to handle increased workload during critical phases.',
      benefits: [
        'Access to specialized expertise (cloud, microservices, etc.)',
        'Quick onboarding into existing teams and processes',
        'Work seamlessly with your internal developers',
        'Flexible duration from weeks to months',
        'No overhead of hiring full-time employees',
        'Knowledge transfer to internal teams'
      ],
      bestFor: [
        'Temporary skill gaps in your team',
        'Scaling for peak workload periods',
        'Specialized projects requiring niche expertise',
        'Mentoring and upskilling internal teams'
      ],
      engagement: 'Hourly or monthly rates per developer'
    },
    {
      icon: <FaClipboardCheck className="text-4xl" />,
      title: 'Fixed-Scope Projects',
      description: 'Well-defined projects with clear requirements delivered on a fixed timeline and budget. Perfect for discrete initiatives with predictable outcomes.',
      benefits: [
        'Predictable costs and timeline',
        'Clear deliverables and milestones',
        'Comprehensive project management',
        'Defined scope with change management process',
        'Quality assurance and testing included',
        'Documentation and knowledge transfer'
      ],
      bestFor: [
        'MVPs and proof-of-concepts',
        'Specific feature development',
        'System migrations or upgrades',
        'Projects with well-defined requirements'
      ],
      engagement: 'Fixed price based on scope and timeline'
    },
    {
      icon: <FaTools className="text-4xl" />,
      title: 'Long-Term Maintenance & Support',
      description: 'Ongoing maintenance, monitoring, and continuous improvement of existing applications. Keep your systems running smoothly and evolving with your business.',
      benefits: [
        '24/7 production monitoring and incident response',
        'Proactive bug fixes and security patches',
        'Performance optimization and improvements',
        'Regular health checks and technical audits',
        'Small feature enhancements and updates',
        'Compliance and security updates'
      ],
      bestFor: [
        'Production applications requiring ongoing support',
        'Legacy systems needing maintenance',
        'Applications with compliance requirements',
        'Systems requiring high availability'
      ],
      engagement: 'Monthly support packages with SLA guarantees'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Engagement Models
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Flexible engagement models designed to fit your specific needs, timeline, and budget.
            All engagements delivered under B2B contracts, compliant with US, Canadian, and EU business requirements.
          </p>
        </div>

        {/* Models Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {models.map((model, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-lg p-8 border border-gray-200 hover:shadow-xl transition-all duration-300"
            >
              <div className="flex items-start space-x-4 mb-6">
                <div className="bg-blue-100 p-4 rounded-lg text-blue-600 flex-shrink-0">
                  {model.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {model.title}
                  </h3>
                  <p className="text-gray-600">
                    {model.description}
                  </p>
                </div>
              </div>

              {/* Benefits */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Key Benefits:</h4>
                <div className="space-y-2">
                  {model.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-start space-x-2">
                      <div className="w-1.5 h-1.5 bg-green-600 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Best For */}
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Best For:</h4>
                <div className="flex flex-wrap gap-2">
                  {model.bestFor.map((item, idx) => (
                    <span
                      key={idx}
                      className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>

              {/* Engagement Type */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-l-4 border-blue-600">
                <div className="font-semibold text-gray-900 mb-1">Engagement Structure:</div>
                <div className="text-gray-700 text-sm">{model.engagement}</div>
              </div>
            </div>
          ))}
        </div>

        {/* B2B Compliance Section */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="flex items-center space-x-3 mb-4">
                <FaShieldAlt className="text-4xl" />
                <h3 className="text-3xl font-bold">B2B Contract Compliance</h3>
              </div>
              <p className="text-blue-100 text-lg mb-6">
                All our engagements are structured as professional B2B contracts, ensuring legal compliance
                and proper business relationships across international borders.
              </p>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <FaGlobe className="text-blue-300" />
                  <span className="text-blue-100">Compliant with US, Canadian, and EU regulations</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FaShieldAlt className="text-blue-300" />
                  <span className="text-blue-100">Professional liability insurance coverage</span>
                </div>
                <div className="flex items-center space-x-3">
                  <FaClipboardCheck className="text-blue-300" />
                  <span className="text-blue-100">Standard NDAs and IP assignment agreements</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">US</div>
                <div className="text-blue-200 text-sm">United States</div>
                <div className="text-blue-100 text-xs mt-2">W-9 & 1099 Ready</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">CA</div>
                <div className="text-blue-200 text-sm">Canada</div>
                <div className="text-blue-100 text-xs mt-2">GST/HST Compliant</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">EU</div>
                <div className="text-blue-200 text-sm">European Union</div>
                <div className="text-blue-100 text-xs mt-2">GDPR Compliant</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center border border-white/20">
                <div className="text-4xl font-bold text-white mb-2">24/7</div>
                <div className="text-blue-200 text-sm">Time Coverage</div>
                <div className="text-blue-100 text-xs mt-2">Overlap Guaranteed</div>
              </div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-6">
            Not sure which engagement model is right for you?
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg"
          >
            Discuss Your Project Requirements
          </button>
        </div>
      </div>
    </section>
  );
};

export default EngagementModels;
